/**
 * BusinessTypeStep Component
 *
 * First step: Select business entity type.
 * All card options are hardcoded (static) - no skeleton needed.
 * Only the selection state comes from user data.
 */

import { Building2, User, Users, Building, Heart } from 'lucide-react';
import type { IMerchant, IOnboardingApplication } from '@order-up/models';
import { useSaveDraft } from '../../lib/hooks';

export interface BusinessTypeStepProps {
  application: IOnboardingApplication;
}

// Business type options - STATIC (hardcoded, no skeleton needed)
const businessTypes = [
  {
    value: 'INDIVSOLE',
    label: 'Sole Proprietorship',
    description: 'You own and operate alone, using your SSN for taxes',
    icon: User
  },
  {
    value: 'LLC',
    label: 'LLC',
    description: 'Limited Liability Company with one or more members',
    icon: Building2
  },
  {
    value: 'PRIVCORP',
    label: 'Corporation',
    description: 'C-Corp or S-Corp with shareholders',
    icon: Building
  },
  {
    value: 'PRTNRSHP',
    label: 'Partnership',
    description: 'General or limited partnership between two or more people',
    icon: Users
  },
  {
    value: 'LLP',
    label: 'LLP',
    description: 'Limited Liability Partnership for professional services',
    icon: Users
  },
  {
    value: 'NONPROFIT',
    label: 'Non-Profit',
    description: '501(c)(3) or other tax-exempt organization',
    icon: Heart
  }
] as const;

const OWNERSHIP_TYPE_LABELS: Record<string, string> = {
  'INDIVSOLE': 'Sole Proprietorship',
  'LLC': 'LLC',
  'PRIVCORP': 'Corporation',
  'PRTNRSHP': 'Partnership',
  'LLP': 'LLP',
  'NONPROFIT': 'Non-Profit'
};

export function BusinessTypeStep({ application }: BusinessTypeStepProps) {
  // Optimistic updates (see packages/client/src/hooks/useOnboarding.ts) apply
  // the selection to the React Query cache instantly, so we intentionally do
  // NOT read `isPending` here. Blocking or dimming the UI while the save is in
  // flight would defeat the optimistic flow and reintroduce the visible "wait"
  // feeling we just eliminated.
  const { mutate: saveDraft } = useSaveDraft();

  const selectedType = application.applicationData.ownership?.ownershipTypeCd;
  const isSoleProp = selectedType === 'INDIVSOLE';

  function selectType(type: typeof businessTypes[number]['value']) {
    const updates: Partial<IMerchant> = {
      ownership: {
        ...application.applicationData.ownership,
        ownershipTypeCd: type
      }
    };

    saveDraft({
      id: application.id,
      data: updates,
      step: application.currentStep
    });
  }

  return (
    <div className="space-y-6">
      {/* Business Type Selection - Cards are static, only selection state is dynamic */}
      <div className="grid gap-3 sm:grid-cols-2">
        {businessTypes.map((type) => {
          const isSelected = selectedType === type.value;

          return (
            <button
              key={type.value}
              type="button"
              onClick={() => selectType(type.value)}
              className={`group flex items-start gap-4 rounded-xl border p-4 text-left transition-all duration-200
                ${
                  isSelected
                    ? 'border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/50'
                    : 'border-border bg-zinc-900/50 hover:border-zinc-600 hover:bg-zinc-800/50'
                }`}
            >
              {/* Radio circle */}
              <div
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors
                  ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-zinc-500'}`}
              >
                {isSelected && <div className="h-2 w-2 rounded-full bg-white"></div>}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{type.label}</span>
                </div>
                <p className="mt-1 text-sm text-muted">{type.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Info callout - conditional on selection */}
      {selectedType && (
        <div className="rounded-lg border border-border/50 bg-zinc-900/30 p-4">
          {isSoleProp ? (
            <p className="text-sm text-zinc-300">
              <span className="font-medium text-white">As a Sole Proprietor:</span>
              {' '}You'll use your Social Security Number (SSN) for tax purposes. No EIN is required, but you
              can use one if you have it.
            </p>
          ) : (
            <p className="text-sm text-zinc-300">
              <span className="font-medium text-white">For {OWNERSHIP_TYPE_LABELS[selectedType]}:</span>
              {' '}You'll need your Employer Identification Number (EIN) and information about any owners with
              25% or more stake in the business.
            </p>
          )}
        </div>
      )}

      {/* Restricted industries notice - STATIC (always show) */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
        <p className="text-sm text-amber-200/80">
          <span className="font-medium text-amber-300">Note:</span>
          {' '}We aren't able to support businesses in certain industries including cryptocurrency, marijuana-related
          companies, money service businesses, and gambling businesses.
        </p>
      </div>
    </div>
  );
}
